import React, {
  useEffect,
  useState,
} from "react";

import {
  FiCalendar,
  FiExternalLink,
  FiPackage,
  FiSave,
  FiTruck,
  FiX,
} from "react-icons/fi";


/* =========================================================
   EMPTY FORM
========================================================= */

const EMPTY_FORM = {
  courierName: "",
  trackingNumber: "",
  trackingUrl: "",
  estimatedDeliveryDate: "",
  shippingNote: "",
  note: "",
};


/* =========================================================
   DATE INPUT VALUE
========================================================= */

const getDateInputValue = (
  value
) => {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
};


/* =========================================================
   COMPONENT
========================================================= */

const ShippingDialog = ({
  open,

  order,

  mode = "ship",

  loading = false,

  onClose,

  onSubmit,
}) => {
  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
  });


  const [
    errors,
    setErrors,
  ] = useState({});


  /* =======================================================
     LOAD ORDER TRACKING
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }


    setForm({
      courierName:
        order
          ?.tracking
          ?.courierName ||
        "",

      trackingNumber:
        order
          ?.tracking
          ?.trackingNumber ||
        "",

      trackingUrl:
        order
          ?.tracking
          ?.trackingUrl ||
        "",

      estimatedDeliveryDate:
        getDateInputValue(
          order
            ?.tracking
            ?.estimatedDeliveryDate
        ),

      shippingNote:
        order
          ?.tracking
          ?.shippingNote ||
        "",

      note: "",
    });


    setErrors({});

  }, [
    open,
    order,
  ]);


  if (!open) {
    return null;
  }


  /* =======================================================
     CHANGE
  ======================================================= */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;


    setForm(
      (previous) => ({
        ...previous,

        [name]:
          value,
      })
    );


    setErrors(
      (previous) => ({
        ...previous,

        [name]:
          "",
      })
    );
  };


  /* =======================================================
     VALIDATE
  ======================================================= */

  const validate =
    () => {
      const nextErrors =
        {};


      if (
        mode ===
        "ship"
      ) {
        if (
          !String(
            form.courierName ||
            ""
          ).trim()
        ) {
          nextErrors.courierName =
            "Courier name is required.";
        }


        if (
          !String(
            form.trackingNumber ||
            ""
          ).trim()
        ) {
          nextErrors.trackingNumber =
            "Tracking / AWB number is required.";
        }
      }


      if (
        form.trackingUrl &&
        !/^https?:\/\//i.test(
          form.trackingUrl
        )
      ) {
        nextErrors.trackingUrl =
          "Tracking URL should start with http:// or https://";
      }


      setErrors(
        nextErrors
      );


      return (
        Object.keys(
          nextErrors
        ).length === 0
      );
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit = (
    event
  ) => {
    event.preventDefault();


    if (
      loading
    ) {
      return;
    }


    if (
      !validate()
    ) {
      return;
    }


    onSubmit?.({
      courierName:
        String(
          form.courierName ||
          ""
        ).trim(),

      trackingNumber:
        String(
          form.trackingNumber ||
          ""
        ).trim(),

      trackingUrl:
        String(
          form.trackingUrl ||
          ""
        ).trim(),

      estimatedDeliveryDate:
        form.estimatedDeliveryDate ||
        "",

      shippingNote:
        String(
          form.shippingNote ||
          ""
        ).trim(),

      note:
        String(
          form.note ||
          ""
        ).trim(),
    });
  };


  /* =======================================================
     TITLE
  ======================================================= */

  const isShipMode =
    mode ===
    "ship";


  return (
    <div
      className="order-dialog-overlay"
    >
      <button
        type="button"
        className="order-dialog-backdrop"
        onClick={
          loading
            ? undefined
            : onClose
        }
        aria-label="Close"
      />


      <div
        className="order-dialog order-shipping-dialog"
        role="dialog"
        aria-modal="true"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="order-dialog-header"
        >
          <div
            className="order-dialog-header-icon shipping"
          >
            <FiTruck />
          </div>


          <div
            className="order-dialog-header-content"
          >
            <span>
              SHIPPING & TRACKING
            </span>

            <h3>
              {isShipMode
                ? "Ship Order"
                : "Update Tracking"}
            </h3>

            <p>
              {order?.orderNumber ||
                "Order"}
            </p>
          </div>


          <button
            type="button"
            className="order-dialog-close"
            onClick={
              onClose
            }
            disabled={
              loading
            }
          >
            <FiX />
          </button>
        </div>


        {/* =================================================
            ORDER INFO
        ================================================= */}

        <div
          className="order-dialog-order-summary"
        >
          <div>
            <FiPackage />

            <span>
              Order
            </span>

            <strong>
              {order?.orderNumber ||
                "-"}
            </strong>
          </div>


          <div>
            <FiTruck />

            <span>
              Current Status
            </span>

            <strong>
              {String(
                order?.status ||
                "-"
              ).replace(
                /_/g,
                " "
              )}
            </strong>
          </div>
        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="order-dialog-body"
          >
            <div
              className="order-dialog-grid"
            >
              {/* ===========================================
                  COURIER
              =========================================== */}

              <div
                className="order-dialog-field"
              >
                <label>
                  Courier Name
                  {isShipMode
                    ? " *"
                    : ""}
                </label>

                <input
                  type="text"
                  name="courierName"
                  value={
                    form.courierName
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="e.g. Delhivery, Blue Dart, DTDC"
                  disabled={
                    loading
                  }
                />

                {errors
                  .courierName ? (
                  <small>
                    {
                      errors
                        .courierName
                    }
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  TRACKING NUMBER
              =========================================== */}

              <div
                className="order-dialog-field"
              >
                <label>
                  Tracking / AWB Number
                  {isShipMode
                    ? " *"
                    : ""}
                </label>

                <input
                  type="text"
                  name="trackingNumber"
                  value={
                    form.trackingNumber
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter tracking number"
                  disabled={
                    loading
                  }
                />

                {errors
                  .trackingNumber ? (
                  <small>
                    {
                      errors
                        .trackingNumber
                    }
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  URL
              =========================================== */}

              <div
                className="order-dialog-field full"
              >
                <label>
                  Tracking URL
                </label>

                <div
                  className="order-dialog-input-icon"
                >
                  <FiExternalLink />

                  <input
                    type="url"
                    name="trackingUrl"
                    value={
                      form.trackingUrl
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="https://courier.example/track/..."
                    disabled={
                      loading
                    }
                  />
                </div>

                {errors
                  .trackingUrl ? (
                  <small>
                    {
                      errors
                        .trackingUrl
                    }
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  ESTIMATED DELIVERY
              =========================================== */}

              <div
                className="order-dialog-field"
              >
                <label>
                  Estimated Delivery
                </label>

                <div
                  className="order-dialog-input-icon"
                >
                  <FiCalendar />

                  <input
                    type="date"
                    name="estimatedDeliveryDate"
                    value={
                      form
                        .estimatedDeliveryDate
                    }
                    onChange={
                      handleChange
                    }
                    disabled={
                      loading
                    }
                  />
                </div>
              </div>
            </div>


            {/* =============================================
                SHIPPING NOTE
            ============================================= */}

            <div
              className="order-dialog-field"
            >
              <label>
                Shipping Note
              </label>

              <textarea
                name="shippingNote"
                rows={3}
                value={
                  form.shippingNote
                }
                onChange={
                  handleChange
                }
                placeholder="Courier instructions or shipping remarks..."
                disabled={
                  loading
                }
              />
            </div>


            {/* =============================================
                STATUS NOTE
            ============================================= */}

            {isShipMode ? (
              <div
                className="order-dialog-field"
              >
                <label>
                  Internal Status Note
                </label>

                <textarea
                  name="note"
                  rows={2}
                  value={
                    form.note
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Optional note for the order timeline..."
                  disabled={
                    loading
                  }
                />
              </div>
            ) : null}


            {/* =============================================
                SHIP INFO
            ============================================= */}

            {isShipMode ? (
              <div
                className="order-dialog-info"
              >
                <FiTruck />

                <div>
                  <strong>
                    Shipping this order
                  </strong>

                  <span>
                    The order status will
                    automatically change from
                    Packed to Shipped after
                    saving these details.
                  </span>
                </div>
              </div>
            ) : null}
          </div>


          {/* =================================================
              FOOTER
          ================================================= */}

          <div
            className="order-dialog-footer"
          >
            <button
              type="button"
              className="secondary"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              className="primary"
              disabled={
                loading
              }
            >
              {loading ? (
                <>
                  <span
                    className="order-button-spinner"
                  />

                  Saving...
                </>
              ) : (
                <>
                  {isShipMode ? (
                    <FiTruck />
                  ) : (
                    <FiSave />
                  )}

                  {isShipMode
                    ? "Ship Order"
                    : "Save Tracking"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default
  ShippingDialog;